// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, AppBar, Typography } from '@mui/material';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// components
import Logo, { LogoProps } from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
//
import AccountPopover from './AccountPopover';
import useAuth from 'src/hooks/useAuth';
import NotificationsPopover from './NotificationsPopover';
import LogoutPopover from './LogoutPopover';
import { m } from 'framer-motion';
import useScrolling from 'src/hooks/useScrolling';
import { useRef } from 'react';
import { invertColor } from 'src/utils/general';
import DOMPurify from 'dompurify';
import { HEADER } from 'src/config';
import useImpersonateContext from 'src/hooks/useImpersonateContext';

// ----------------------------------------------------------------------

type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
  verticalLayout: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})<RootStyleProps>(({ theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height', 'top'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    width: '100%',
    backgroundColor: theme.palette.primary,
    boxShadow: theme.customShadows.header,
  },
}));

// ----------------------------------------------------------------------

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
  verticalLayout?: boolean;
};

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
}: Props) {
  const theme = useTheme();
  const { user } = useAuth();

  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const isDesktop = useResponsive('up', 'lg');

  const { headerCollapsed } = useScrolling();

  const { bannerOffset } = useImpersonateContext();

  const headerBoxRef = useRef<any>();
  const subHeaderBoxRef = useRef<any>();

  const headerVariant = headerCollapsed ? 'collapsed' : 'expanded';

  const iconColor =
    user && user.customer?.color
      ? invertColor(user.customer.color, true)
      : theme.palette.primary.contrastText;

  const iconSize = headerCollapsed ? 'small' : 'medium';

  return (
    <RootStyle
      isCollapse={isCollapse}
      isOffset={isOffset}
      verticalLayout={verticalLayout}
      sx={{ top: bannerOffset }}
    >
      <m.div
        variants={{
          collapsed: { height: HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT_COLLAPSED },
          expanded: { height: HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT },
        }}
        initial={false}
        animate={headerVariant}
        transition={{ duration: 0.1 }}
        ref={headerBoxRef}
      >
        <Box
          sx={{
            height: 1,
            backgroundColor: user?.bgColor,
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {user && user.customer && user.customer.banner ? (
            <Box
              sx={{
                width: 0.8,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                ml: 11,
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(user.customer.banner) }}
            />
          ) : (
            <Box
              sx={{
                width: 0.8,
                display: 'flex',
                ml: 11,
                alignItems: 'center',
                height: 1,
              }}
            >
              <Logo
                color={LogoProps.LIGHT}
                width={headerCollapsed ? 100 : 170}
                height={headerCollapsed ? 20 : 50}
              />
            </Box>
          )}

          {/* Icon section */}
          <Box
            sx={{
              width: '20%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              pr: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
              <NotificationsPopover color={iconColor} size={iconSize} />
              <AccountPopover color={iconColor} size={iconSize} />
              <LogoutPopover color={iconColor} size={iconSize} />
            </Stack>
          </Box>
        </Box>

        <m.div
          variants={{
            collapsed: {
              opacity: 0,
              height: HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT_COLLAPSED,
            },
            expanded: {
              opacity: 1,
              height: HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT,
            },
          }}
          initial={false}
          animate={headerVariant}
          transition={{ duration: 0.1 }}
          ref={subHeaderBoxRef}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              pr: { lg: 5 },
              pl: { lg: 5 },
              backgroundColor: '#f6f7f9',
              boxShadow: '2px 2px 10px 0px rgba(0, 0, 0, 0.15)',
              height: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              {user && user.parentLogo && (
                <Box sx={{ height: '80%', mr: 2 }}>
                  <img
                    style={{
                      height: `${HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT - 10}px`,
                    }}
                    src={user.parentLogo}
                    alt="logo"
                  />
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: theme.palette.primary.main }}
              >
                Powered by{' '}
              </Typography>
              <Logo disabledLink={false} color={LogoProps.MAIN} height={20} width={100} />
            </Box>
          </Box>
        </m.div>

        {isDesktop && verticalLayout && (
          <Logo disabledLink={false} color={LogoProps.LIGHT} sx={{ mr: 2.5 }} />
        )}

        {!isDesktop && (
          <IconButtonAnimate
            onClick={onOpenSidebar}
            sx={{ mr: 1, color: 'text.primary' }}
          >
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}
      </m.div>
    </RootStyle>
  );
}